import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import Header from "Header";
import { useLocal } from "hooks";
import { Button, FormControl, TextField } from "_imports/mui";
import {
  useGetExamExtraNumQuestionsQuery,
  useGetExamNumAnswersQuery,
  useGetExamNumQuestionsQuery,
  useGetExamResponseQuery,
  useUpdateExamResponseMutation,
} from "store/api";
import type { Response } from "store/type";

import { QUESTIONS_PER_BLOCK } from "./SubmitAnwers.constants";
import { FROM_CREATE, Key, LETTER_TO_NUM, Paths } from "../constants";

import "App.css";
import "./SubmitAnswers.css";

const SubmitAnswers = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const userLocal = useLocal(Key.USER_ID);
  const userId = userLocal.getItem();
  const [updateAnswer] = useUpdateExamResponseMutation();

  const {
    data: questions = 0,
    error: numQuestionsError,
    isLoading: isNumQuestionsLoading,
  } = useGetExamNumQuestionsQuery("_", {
    skip: userId == null || typeof userId !== "string",
  });

  const { data: extraQuestions = 0 } = useGetExamExtraNumQuestionsQuery("_", {
    skip: userId == null || typeof userId !== "string",
  });

  const { data: numAnswers = 0, error: numAnswersError } =
    useGetExamNumAnswersQuery("_", {
      skip: userId == null || typeof userId !== "string",
    });

  const {
    data: alreadySetAnswers,
    error: examResponseError,
    isLoading: isAlreadySetAnswersLoading,
    refetch: refetchAlreadySetAnswers,
    isUninitialized: isUninitializedAlreadySetAnswers,
  } = useGetExamResponseQuery(userId, {
    skip:
      userId == null ||
      typeof userId !== "string" ||
      state?.from === FROM_CREATE,
    refetchOnMountOrArgChange: true,
  });

  const getErrorHandler = (err: any, additionalMessage = "") => {
    let message = additionalMessage;
    if (err.status === "FETCH_ERROR") {
      message += "No se puede alcanzar el servidor backend.";
    } else if (err.data?.detail != null) {
      message += `Error: ${err.data.detail}`;
    } else {
      message = "Por favor, inténtalo más tarde.";
    }
    enqueueSnackbar(message, {
      variant: "error",
    });
  };

  useEffect(() => {
    if (numQuestionsError) {
      getErrorHandler(
        numQuestionsError,
        "Error al obtener el número de preguntas. "
      );
    }
  }, [numQuestionsError]);

  useEffect(() => {
    if (numAnswersError) {
      getErrorHandler(
        numAnswersError,
        "Error al obtener el número de opciones. "
      );
    }
  }, [numAnswersError]);

  useEffect(() => {
    if (examResponseError) {
      getErrorHandler(
        examResponseError,
        "Error al recuperar las respuestas guardadas anteriormente. "
      );
    }
  }, [examResponseError]);

  const defaultAnswers = useMemo(() => {
    if (alreadySetAnswers != null) {
      if (!Array.isArray(alreadySetAnswers.responses)) {
        enqueueSnackbar(
          `Se esperaba que las respuestas guardadas anteriormente fueran un array. Se recibió ${alreadySetAnswers}}`,
          {
            variant: "warning",
            preventDuplicate: true,
          }
        );
        return [];
      }
      if (
        !isNumQuestionsLoading &&
        !isAlreadySetAnswersLoading &&
        alreadySetAnswers.responses.length !== questions
      ) {
        enqueueSnackbar(
          "El número de preguntas no coincide con la longitud de las respuestas guardadas del usuario. Por favor, notifica al administrador para verificar la base de datos. El número de preguntas guardadas será ignorado.",
          {
            variant: "warning",
            preventDuplicate: true,
          }
        );
        return [];
      }
      return alreadySetAnswers.responses;
    }
    return Array.from(Array(questions)).map((_, index) => ({
      question_number: index + 1,
      answer: null,
    }));
  }, [questions, alreadySetAnswers]);

  useEffect(() => {
    if (defaultAnswers.length > 0) {
      setAnswers(defaultAnswers);
    }
  }, [defaultAnswers]);

  useEffect(() => {
    if (alreadySetAnswers?.user_name) {
      setName(alreadySetAnswers.user_name);
    }
  }, [alreadySetAnswers?.user_name]);

  const [answers, setAnswers] = useState<Response[]>(
    alreadySetAnswers?.responses ?? defaultAnswers
  );

  const [name, setName] = useState<string>("");
  const [errorHelperText, setErrorHelperText] = useState<string>("");

  useEffect(() => {
    if (userId == null) {
      enqueueSnackbar(
        "Error al recuperar el ID de usuario. Por favor, inicia sesión de nuevo.",
        {
          variant: "warning",
          preventDuplicate: true,
        }
      );
      navigate(Paths.SIGN_IN);
    }
  }, []);

  const updateAnswers = (answer: string | null, question_number: number) => {
    const newRes = JSON.parse(JSON.stringify(answers));
    newRes[question_number].answer = answer == null ? null : Number(answer);
    setAnswers(newRes);
  };

  const onClickRadio = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    question_number: number
  ) => {
    // @ts-ignore
    if (e.target.value == answers[question_number].answer) {
      updateAnswers(null, question_number);
    }
  };

  const sendRequest = async () => {
    if (name.length === 0) {
      setErrorHelperText("Por favor, introduce tu nombre completo");
      return;
    }
    setErrorHelperText("");
    return await updateAnswer({
      user_id: userId,
      user_name: name,
      responses: answers,
    })
      .unwrap()
      .then(() => {
        enqueueSnackbar("Respuestas guardadas con éxito.", {
          variant: "success",
        });
        !isUninitializedAlreadySetAnswers && refetchAlreadySetAnswers();
        return true;
      })
      .catch((err) => {
        let message: string;
        if (err.status === "FETCH_ERROR") {
          message = "No se puede alcanzar el servidor backend.";
        } else if (err.data?.detail != null) {
          message = `Error al guardar las respuestas. Error: ${err.data.detail}`;
        } else {
          message =
            "Error al guardar las respuestas. Por favor, inténtalo de nuevo.";
        }
        enqueueSnackbar(message, {
          variant: "error",
        });
        return false;
      });
  };

  const onSubmit = async () => {
    const status = await sendRequest();
    if (status) {
      navigate(Paths.COMPARE, { replace: true });
    }
  };

  const splitAnswer = useMemo(() => {
    if (!Number.isInteger(questions)) {
      return [[]];
    }
    const createIntegerList = (start: number, end: number) =>
      Array.from(Array(end - start + 1).keys()).map((i) => i + start);

    const mandatoryQuestions = questions - extraQuestions;
    const results: number[][] = [];
    let index = 1;
    while (index <= mandatoryQuestions) {
      const remaining = Math.min(
        mandatoryQuestions,
        index + QUESTIONS_PER_BLOCK - 1
      );
      results.push(createIntegerList(index, remaining));
      index += QUESTIONS_PER_BLOCK;
    }
    index = mandatoryQuestions + 1;
    while (index <= questions) {
      const remaining = Math.min(questions, index + QUESTIONS_PER_BLOCK - 1);
      results.push(createIntegerList(index, remaining));
      index += QUESTIONS_PER_BLOCK;
    }

    return results;
  }, [extraQuestions, questions]);

  return (
    <div className="submit-container">
      <Header
        userIdentifier={userId}
        showSaveIdInfo={true}
        showSignOut={true}
      />
      {questions === 0 && <p>No se encontraron preguntas.</p>}
      {numAnswers === 0 && <p>Número de opciones desconocido.</p>}
      {questions !== 0 && numAnswers !== 0 && (
        <>
          <div className="submit-form">
            <FormControl className="center">
              <div style={{ height: "96px" }}>
                <TextField
                  id="outlined-basic"
                  label="Nombre completo"
                  variant="outlined"
                  autoFocus
                  autoComplete="on"
                  margin="normal"
                  error={errorHelperText.length > 0}
                  helperText={errorHelperText}
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              </div>
              <div className="submit-block-container">
                {splitAnswer.map((batch, pindex) => {
                  return (
                    <div className="submit-block" key={pindex}>
                      {batch.map((val) => {
                        const questionNum = val;
                        const index = val - 1;
                        return (
                          <div key={questionNum} className="center">
                            <label
                              htmlFor={`question${questionNum}`}
                              className="question-number"
                            >
                              <span
                                style={{
                                  opacity:
                                    questionNum > questions - extraQuestions
                                      ? 0.4
                                      : 1,
                                }}
                              >
                                {questionNum}:
                              </span>
                            </label>
                            <div
                              role="radiogroup"
                              aria-labelledby={`question${questionNum}`}
                              className="answer-options-container"
                              style={{
                                backgroundColor:
                                  alreadySetAnswers != null &&
                                  answers[index]?.answer !==
                                    alreadySetAnswers.responses[index]?.answer
                                    ? "#d1ffbd"
                                    : "",
                              }}
                            >
                              {Object.entries(LETTER_TO_NUM)
                                .slice(0, numAnswers)
                                .map(([key, value]) => (
                                  <label key={key} className="answer-option">
                                    <span className="answer-option-input-span">
                                      <input
                                        type="radio"
                                        name={`question${questionNum}`}
                                        value={value}
                                        checked={
                                          answers[index]?.answer === value
                                        }
                                        onChange={(e) =>
                                          updateAnswers(e.target.value, index)
                                        }
                                        onClick={(e) => onClickRadio(e, index)}
                                        className="answer-option-input"
                                      />
                                    </span>
                                    <span className="answer-option-label">
                                      {key}
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </FormControl>
          </div>
          <div className="footer">
            <Button
              variant="contained"
              onClick={sendRequest}
              style={{ margin: "0 1rem" }}
            >
              Guardar y seguir respondiendo
            </Button>
            <Button variant="contained" onClick={onSubmit}>
              Enviar y verificar
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SubmitAnswers;
